import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Calendar, CreditCard, Info, RefreshCw } from 'lucide-react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';

import CustomerNavbar from '../components/CustomerNavbar';
import Footer from '../components/Footer';
import apiService from '../services/api';
import { useAuth } from '../hooks/useAuth';

type DepositInfo = {
  appointmentId: string;
  requestedAppointmentId?: string;
  requiredDepositAmount: number | null;
  receivedDepositAmount: number;
  amountDueCents: number;
  amountDueDollars: number;
  alreadyPaid: boolean;
  businessName: string | null;
  isSeriesDeposit?: boolean;
  seriesId?: string | null;
  seriesOccurrenceCount?: number | null;
  perOccurrenceDepositAmount?: number | null;
};

function CheckoutForm(props: { paymentAppointmentId: string; returnAppointmentId: string }) {
  const { paymentAppointmentId, returnAppointmentId } = props;
  const navigate = useNavigate();

  const stripe = useStripe();
  const elements = useElements();

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);

      if (!stripe || !elements) return;

      try {
        setSubmitting(true);
        const result = await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: `${window.location.origin}/dashboard/customer/appointments/${encodeURIComponent(returnAppointmentId)}/deposit/complete`,
          },
          redirect: 'if_required',
        });

        if (result.error) {
          setError(result.error.message || 'Payment failed. Please try again.');
          return;
        }

        const pi = result.paymentIntent;
        if (pi?.id) {
          try {
            await apiService.syncAppointmentDepositPaymentIntent(paymentAppointmentId, pi.id);
          } catch {
            // Non-fatal; completion page will retry.
          }
          navigate(
            `/dashboard/customer/appointments/${encodeURIComponent(returnAppointmentId)}/deposit/complete?payment_intent=${encodeURIComponent(pi.id)}`
          );
        } else {
          navigate(`/dashboard/customer/appointments/${encodeURIComponent(returnAppointmentId)}`);
        }
      } catch (err: any) {
        setError(err?.message || 'Payment failed. Please try again.');
      } finally {
        setSubmitting(false);
      }
    },
    [elements, paymentAppointmentId, returnAppointmentId, navigate, stripe]
  );

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="bg-white border border-gray-200 rounded-2xl p-4">
          <PaymentElement />
        </div>

        {error ? <div className="text-sm text-red-600">{error}</div> : null}

        <button
          type="submit"
          className="aacore-btn-primary w-full justify-center"
          disabled={!stripe || !elements || submitting}
        >
          <CreditCard className="w-4 h-4" />
          {submitting ? 'Processing…' : 'Pay deposit now'}
        </button>

        <div className="text-xs text-gray-500">
          Payments are processed securely by Stripe. Do not refresh the page while processing.
        </div>
      </form>

      {submitting ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          aria-modal="true"
          role="dialog"
          aria-live="polite"
          aria-busy="true"
        >
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 max-w-sm w-full text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B4778] mx-auto mb-4" aria-hidden />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Processing your payment</h2>
            <p className="text-sm text-gray-600 mb-0">
              Please wait while your deposit is being processed. Do not refresh or close this page.
            </p>
          </div>
        </div>
      ) : null}
    </>
  );
}

const CustomerAppointmentDepositPage: React.FC = () => {
  const navigate = useNavigate();
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const { logout } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [depositInfo, setDepositInfo] = useState<DepositInfo | null>(null);

  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const cfg = await apiService.getStripeConfig();
        setStripePromise(loadStripe(cfg.publishableKey));
      } catch (e: any) {
        console.error('Failed to load Stripe config', e);
        setError(e?.response?.data?.error || e?.message || 'Stripe is not configured');
      }
    })();
  }, []);

  const load = useCallback(async () => {
    if (!appointmentId) return;
    try {
      setLoading(true);
      setError(null);
      const info = await apiService.getAppointmentDeposit(appointmentId);
      setDepositInfo(info as DepositInfo);

      if (info.alreadyPaid) {
        setClientSecret(null);
        return;
      }
      if (info.amountDueCents <= 0) {
        setClientSecret(null);
        return;
      }

      const pi = await apiService.createAppointmentDepositPaymentIntent(appointmentId);
      setClientSecret(pi.clientSecret);
    } catch (e: any) {
      console.error('Failed to load appointment deposit', e);
      setDepositInfo(null);
      setClientSecret(null);
      setError(e?.response?.data?.error || 'Failed to load deposit information');
    } finally {
      setLoading(false);
    }
  }, [appointmentId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (depositInfo?.alreadyPaid) toast('This appointment deposit has already been paid.');
  }, [depositInfo?.alreadyPaid]);

  if (!appointmentId) {
    return (
      <div className="min-h-screen no-animations flex flex-col relative">
        <CustomerNavbar onLogout={logout} showDashboardLink={true} showLogoutButton={true} />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center max-w-md">
            <Calendar className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Appointment Not Found</h1>
            <p className="text-gray-600 mb-6">Missing appointment ID.</p>
            <button onClick={() => navigate('/dashboard/customer/appointments')} className="aacore-btn-primary">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Appointments
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen no-animations flex flex-col relative">
        <CustomerNavbar onLogout={logout} showDashboardLink={true} showLogoutButton={true} />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading deposit information…</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !depositInfo) {
    return (
      <div className="min-h-screen no-animations flex flex-col relative">
        <CustomerNavbar onLogout={logout} showDashboardLink={true} showLogoutButton={true} />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center max-w-md">
            <Calendar className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Unable to Load Deposit</h1>
            <p className="text-gray-600 mb-6">{error || 'Deposit information is unavailable'}</p>
            <div className="flex gap-2 justify-center">
              <button onClick={() => load()} className="aacore-btn-primary inline-flex items-center gap-2">
                <RefreshCw className="w-4 h-4" />
                Try again
              </button>
              <button
                onClick={() => navigate(`/dashboard/customer/appointments/${encodeURIComponent(appointmentId)}`)}
                className="aacore-btn-outlined inline-flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to appointment
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const businessName = depositInfo.businessName || 'The business';
  const amountFormatted = depositInfo.amountDueDollars != null ? `$${Number(depositInfo.amountDueDollars).toFixed(2)}` : '$0.00';
  const paymentAppointmentId = depositInfo.appointmentId;
  const isSeriesDeposit = depositInfo.isSeriesDeposit === true;
  const seriesOccurrenceCount = depositInfo.seriesOccurrenceCount;
  const perOccurrenceDepositAmount = depositInfo.perOccurrenceDepositAmount;

  if (depositInfo.alreadyPaid || depositInfo.amountDueCents <= 0) {
    return (
      <div className="min-h-screen no-animations flex flex-col relative">
        <CustomerNavbar onLogout={logout} showDashboardLink={true} showLogoutButton={true} />
        <div className="flex-1 pt-6 pb-12 overflow-visible">
          <div className="px-4 lg:px-8 max-w-2xl mx-auto">
            <div className="mb-4">
              <Link
                to={`/dashboard/customer/appointments/${encodeURIComponent(appointmentId)}`}
                className="flex items-center font-semibold"
                style={{ color: '#0B4778' }}
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Appointment
              </Link>
            </div>
            <div className="bg-white border border-gray-200 rounded-2xl p-6 text-center">
              <h1 className="text-xl font-bold text-gray-900 mb-2">No deposit due</h1>
              <p className="text-gray-600">
                {depositInfo.alreadyPaid
                  ? 'The deposit for this appointment has already been paid.'
                  : 'This appointment does not require a deposit.'}
              </p>
              <Link
                to={`/dashboard/customer/appointments/${encodeURIComponent(appointmentId)}`}
                className="aacore-btn-primary mt-4 inline-flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to appointment
              </Link>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!stripePromise || !clientSecret) {
    return (
      <div className="min-h-screen no-animations flex flex-col relative">
        <CustomerNavbar onLogout={logout} showDashboardLink={true} showLogoutButton={true} />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center max-w-md">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Unable to Start Payment</h1>
            <p className="text-gray-600 mb-6">Payment could not be initialized. Please try again or contact the business.</p>
            <button onClick={() => load()} className="aacore-btn-primary inline-flex items-center gap-2">
              <RefreshCw className="w-4 h-4" />
              Try again
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen no-animations flex flex-col relative">
      <CustomerNavbar onLogout={logout} showDashboardLink={true} showLogoutButton={true} />

      <div className="flex-1 pt-6 pb-12 overflow-visible">
        <div className="px-4 lg:px-8 max-w-3xl mx-auto overflow-visible">
          <div className="mb-4">
            <Link
              to={`/dashboard/customer/appointments/${encodeURIComponent(appointmentId)}`}
              className="flex items-center font-semibold"
              style={{ color: '#0B4778' }}
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Appointment
            </Link>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl p-4 mb-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs text-gray-500 mb-1">PAY APPOINTMENT DEPOSIT</div>
                <h1 className="text-xl font-bold mb-0">
                  {isSeriesDeposit ? 'Recurring series deposit due' : 'Deposit due'}
                </h1>
                <p className="text-gray-600 text-sm mb-0">{businessName}</p>
              </div>
              <div className="text-right">
                <div className="text-xs text-gray-500 mb-1">AMOUNT DUE</div>
                <div className="text-2xl font-bold">{amountFormatted}</div>
              </div>
            </div>

            <div className="mt-3 p-3 border border-gray-200 rounded-xl bg-gray-50 text-sm text-gray-600 flex gap-2">
              <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" />
              <div>
                {isSeriesDeposit && seriesOccurrenceCount != null && perOccurrenceDepositAmount != null ? (
                  <>
                    This deposit covers all{' '}
                    <span className="font-semibold text-gray-900">{seriesOccurrenceCount}</span> appointments in your
                    recurring series (${Number(perOccurrenceDepositAmount).toFixed(2)} per visit). Pay once to confirm
                    the entire series.
                  </>
                ) : null}
                {isSeriesDeposit && seriesOccurrenceCount != null && perOccurrenceDepositAmount != null ? (
                  <p className="mt-2 mb-0">
                    You&apos;re paying a deposit to <span className="font-semibold text-gray-900">{businessName}</span>.
                    If you have questions, contact the business before paying.
                  </p>
                ) : (
                  <>
                    You&apos;re paying a deposit to <span className="font-semibold text-gray-900">{businessName}</span>. If you have questions, contact the
                    business before paying.
                  </>
                )}
              </div>
            </div>
          </div>

          <Elements
            stripe={stripePromise}
            options={{
              clientSecret,
              appearance: { theme: 'stripe' },
            }}
          >
            <CheckoutForm paymentAppointmentId={paymentAppointmentId} returnAppointmentId={appointmentId} />
          </Elements>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CustomerAppointmentDepositPage;
